// racerEngine.js — MiST · FORMULA 1 (pseudo-3D top-down racer).
// Self-contained high-framerate canvas engine: an infinite vertical scrolling
// road, a player car steered by an (acceleration / braking / drift) vector,
// rival-car obstacle blocks, and boost-pad speed gates. Owns its own RAF loop
// and keyboard+pointer input; no dependency on the strategy-RPG engines.

const DPR = Math.max(1, Math.min(3, typeof window !== "undefined" ? window.devicePixelRatio : 1));

const SPAWN_DIST = 120;    // how far ahead (world units) a row spawns
const ROW_GAP = 40;        // world-unit spacing between spawned rows
const LANE_SPAN = 0.74;    // max normalised lane offset from centre
const LANES = [-0.5, 0, 0.5];

const rnd = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export class RacerEngine {
  constructor(canvas, {
    onUpdate, onFinish, onGameOver, sprites, autopilot, durationMs,
    speed, trafficDensity, boostPads, rivalType,
  } = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.onUpdate = onUpdate || (() => {});
    this.onFinish = onFinish || (() => {});
    this.onGameOver = onGameOver || (() => {});
    this.images = new Map();
    this.backdrop = null;
    this.sprites = sprites || {};              // {car} blob url (optional)
    if (this.sprites.car) this._img(this.sprites.car);

    this.autopilot = !!autopilot;
    this.durationMs = durationMs || 0;
    this.trafficDensity = trafficDensity || "moderate"; // low | moderate | extreme
    this.boostPads = boostPads !== "off";               // boolean
    this.rivalType = rivalType || "sports";             // sports | police | trucks

    // ── Steering vector state (the control model) ──────────────────────────
    this.baseSpeed = speed || 80;   // cruise speed (world units / sec)
    this.maxSpeed = this.baseSpeed * 1.9;
    this.minSpeed = this.baseSpeed * 0.35;
    this.speedVal = this.baseSpeed;   // current forward speed
    this.acceleration = 0;            // 0..1 throttle input
    this.braking = 0;                 // 0..1 brake input
    this.drift = 0;                   // -1..1 lateral drift momentum
    this.boostUntil = 0;

    this.laneNorm = 0;                // current lateral position (-LANE_SPAN..LANE_SPAN)
    this.targetLane = 0;              // steer target
    this.distance = 0;
    this.nextRowAt = 30;
    this.entities = [];               // rivals + boost pads
    this.particles = [];
    this.dashOff = 0;                 // scrolling lane-dash phase
    this.shake = 0;
    this.crashes = 0;
    this.over = false;
    this._t0 = 0;
    this.w = 0; this.h = 0; this.raf = null; this.last = 0;

    this._keys = {};
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._down = this._down.bind(this);
    this._move = this._move.bind(this);
    this._up = this._up.bind(this);
    this._loop = this._loop.bind(this);
  }

  mount() {
    this.resize();
    this.canvas.addEventListener("pointerdown", this._down);
    this.canvas.addEventListener("pointermove", this._move);
    window.addEventListener("pointerup", this._up);
    window.addEventListener("keydown", this._onKeyDown);
    window.addEventListener("keyup", this._onKeyUp);
    this.last = performance.now();
    this._t0 = this.last;
    this._emit();
    this.raf = requestAnimationFrame(this._loop);
  }
  unmount() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointerdown", this._down);
    this.canvas.removeEventListener("pointermove", this._move);
    window.removeEventListener("pointerup", this._up);
    window.removeEventListener("keydown", this._onKeyDown);
    window.removeEventListener("keyup", this._onKeyUp);
  }
  resize() {
    const r = this.canvas.getBoundingClientRect();
    this.w = r.width; this.h = r.height;
    this.canvas.width = Math.round(r.width * DPR);
    this.canvas.height = Math.round(r.height * DPR);
    this.ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }
  setBackdrop(url) { this.backdrop = url || null; if (url) this._img(url); }
  _img(url) {
    if (!url) return null;
    let im = this.images.get(url);
    if (!im) { im = new Image(); im.__r = false; im.onload = () => { im.__r = true; }; im.src = url; this.images.set(url, im); }
    return im.__r ? im : null;
  }
  _emit() { this.onUpdate({ speed: Math.round(this.speedVal), distance: Math.round(this.distance), boosting: performance.now() < this.boostUntil }); }

  // ── Input ─────────────────────────────────────────────────────────────────
  _onKeyDown(e) {
    const k = e.key.toLowerCase();
    if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(k)) e.preventDefault();
    this._keys[k] = true;
    if (k === "arrowleft" || k === "a") this.targetLane = clamp(this.targetLane - 0.5, -LANE_SPAN, LANE_SPAN);
    if (k === "arrowright" || k === "d") this.targetLane = clamp(this.targetLane + 0.5, -LANE_SPAN, LANE_SPAN);
  }
  _onKeyUp(e) { this._keys[e.key.toLowerCase()] = false; }
  _pointerLane(clientX) {
    const rect = this.canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    this.targetLane = clamp(((px - this.w / 2) / (this.w * 0.42)) * LANE_SPAN, -LANE_SPAN, LANE_SPAN);
  }
  _down(e) { this.dragging = true; this._touchAccel = true; this._pointerLane(e.clientX); }
  _move(e) { if (this.dragging) this._pointerLane(e.clientX); }
  _up() { this.dragging = false; this._touchAccel = false; }

  // ── Perspective helpers ────────────────────────────────────────────────────
  get _horizonY() { return this.h * 0.22; }
  get _playerY() { return this.h * 0.82; }
  _progressOf(dz) { return clamp(1 - dz / SPAWN_DIST, 0, 1); }
  _screen(dz, laneNorm) {
    const p = this._progressOf(dz);
    const y = this._horizonY + (this._playerY - this._horizonY) * p;
    const half = 8 + (this.w * 0.46 - 8) * p;
    const x = this.w / 2 + laneNorm * half;
    const scale = 0.16 + 0.84 * p;
    return { x, y, scale, p };
  }

  // ── Spawning ────────────────────────────────────────────────────────────────
  _spawnRow() {
    const freq = { low: 0.4, moderate: 0.62, extreme: 0.9 }[this.trafficDensity] ?? 0.62;
    if (this.boostPads && Math.random() < 0.22) {
      // Boost-pad speed gate on a random lane.
      const ln = LANES[Math.floor(Math.random() * LANES.length)];
      this.entities.push({ type: "boost", dz: SPAWN_DIST, laneNorm: ln });
      return;
    }
    if (Math.random() < freq) {
      // Rival-car obstacle row — leave at least one lane open.
      const blocked = new Set();
      const count = this.trafficDensity === "extreme" ? (Math.random() < 0.5 ? 2 : 1) : 1;
      while (blocked.size < count) blocked.add(LANES[Math.floor(Math.random() * LANES.length)]);
      for (const ln of blocked) {
        this.entities.push({ type: "rival", dz: SPAWN_DIST, laneNorm: ln, hue: rnd(0, 360), color: this._rivalColor(), rel: rnd(0.18, 0.42) });
      }
    }
  }
  _rivalColor() {
    if (this.rivalType === "police") return Math.random() < 0.5 ? "#1846c9" : "#e8eef7";
    if (this.rivalType === "trucks") return "#8a6d3b";
    return ["#ff3b4e", "#ffb020", "#25e06a", "#b061ff"][Math.floor(Math.random() * 4)];
  }

  _burst(x, y, color, n = 12) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, s = rnd(40, 170);
      this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, color });
    }
  }

  _end(win) {
    if (this.over) return;
    this.over = true;
    win ? this.onFinish({ distance: Math.round(this.distance), speed: Math.round(this.speedVal) })
        : this.onGameOver({ distance: Math.round(this.distance) });
  }

  // ── Update ──────────────────────────────────────────────────────────────────
  _update(dt) {
    if (this.over) return;
    const sec = dt / 1000;
    const now = performance.now();

    if (this.durationMs && this._t0 && (now - this._t0) >= this.durationMs) { this._end(true); return; }

    // resolve control inputs into the steering vector
    if (this.autopilot) this._autoDrive();
    else {
      const K = this._keys;
      this.acceleration = (K["arrowup"] || K["w"] || this._touchAccel) ? 1 : 0.55; // idle throttle keeps it moving
      this.braking = (K["arrowdown"] || K["s"]) ? 1 : 0;
      this.drift = (K[" "]) ? Math.sign(this.targetLane - this.laneNorm) : 0;
    }

    // speed integration (throttle up, brake/drag down), boost overrides toward max
    const boosting = now < this.boostUntil;
    const targetSpeed = boosting ? this.maxSpeed
      : this.minSpeed + (this.maxSpeed - this.minSpeed) * (this.acceleration * (1 - this.braking * 0.85));
    this.speedVal += (targetSpeed - this.speedVal) * Math.min(1, dt / 320);
    this.speedVal = clamp(this.speedVal, this.minSpeed, this.maxSpeed);

    // lateral steering with drift momentum
    const steerRate = Math.min(1, dt / (this.drift ? 160 : 90));
    this.laneNorm += (this.targetLane - this.laneNorm) * steerRate + this.drift * 0.006 * dt;
    this.laneNorm = clamp(this.laneNorm, -LANE_SPAN, LANE_SPAN);

    this.distance += this.speedVal * sec;
    this.dashOff = (this.dashOff + this.speedVal * sec * 0.06) % 1;
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt);

    if (this.distance >= this.nextRowAt) { this._spawnRow(); this.nextRowAt += ROW_GAP; }

    // advance entities toward camera (rivals close slower — they also move forward)
    for (const en of this.entities) {
      const rel = en.type === "rival" ? (1 - en.rel) : 1;
      en.dz -= this.speedVal * sec * rel;
      if (en.dz <= 0 && !en.resolved) {
        en.resolved = true;
        const hit = Math.abs(en.laneNorm - this.laneNorm) < 0.26;
        const s = this._screen(0, en.laneNorm);
        if (en.type === "boost" && hit) {
          this.boostUntil = now + 1200;
          this._burst(s.x, s.y, "#00e5ff", 16);
          this._emit();
        } else if (en.type === "rival" && hit) {
          this.crashes += 1;
          this.speedVal = this.minSpeed;
          this.shake = 320;
          this._burst(s.x, s.y, "#ff5a3c", 20);
          this._emit();
          if (this.crashes >= 5 && !this.autopilot) { this._end(false); return; }
        }
      }
    }
    this.entities = this.entities.filter((e) => e.dz > -10);

    for (const p of this.particles) { p.x += p.vx * sec; p.y += p.vy * sec; p.life -= sec * 1.6; }
    this.particles = this.particles.filter((p) => p.life > 0);
  }

  _autoDrive() {
    this.acceleration = 1; this.braking = 0;
    // seek boost pads, else steer to the nearest open lane away from close rivals.
    let boost = null, threat = null;
    for (const en of this.entities) {
      if (en.resolved || en.dz <= 4 || en.dz > 90) continue;
      if (en.type === "boost") { if (!boost || en.dz < boost.dz) boost = en; }
      else if (en.type === "rival") { if (!threat || en.dz < threat.dz) threat = en; }
    }
    if (boost && (!threat || boost.dz <= threat.dz + 8)) { this.targetLane = boost.laneNorm; this.drift = 0; return; }
    if (threat && Math.abs(threat.laneNorm - this.laneNorm) < 0.3) {
      // pick an open lane
      const open = LANES.filter((ln) => !this.entities.some((e) => e.type === "rival" && !e.resolved && e.dz < 90 && Math.abs(e.laneNorm - ln) < 0.2));
      const pick = open.length ? open.reduce((a, b) => (Math.abs(b - this.laneNorm) < Math.abs(a - this.laneNorm) ? b : a)) : (threat.laneNorm > 0 ? -0.5 : 0.5);
      this.targetLane = pick;
      this.drift = Math.sign(pick - this.laneNorm);
    } else { this.drift = 0; }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  _render() {
    const ctx = this.ctx, w = this.w, h = this.h;
    ctx.save();
    if (this.shake > 0) { const m = this.shake / 120; ctx.translate(rnd(-m, m) * 3, rnd(-m, m) * 3); }
    ctx.clearRect(-8, -8, w + 16, h + 16);

    // sky / backdrop
    const bg = this._img(this.backdrop);
    if (bg) {
      const iw = bg.naturalWidth || 1, ih = bg.naturalHeight || 1;
      const s = Math.max(w / iw, h / ih);
      ctx.globalAlpha = 0.5; ctx.drawImage(bg, w / 2 - (iw * s) / 2, 0, iw * s, ih * s); ctx.globalAlpha = 1;
    } else {
      const sky = ctx.createLinearGradient(0, 0, 0, this._horizonY);
      sky.addColorStop(0, "#10203f"); sky.addColorStop(1, "#3a2a55");
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, this._horizonY);
      // distant sun glow
      ctx.fillStyle = "rgba(255,160,80,0.35)";
      ctx.beginPath(); ctx.arc(w / 2, this._horizonY, w * 0.22, Math.PI, 0); ctx.fill();
    }

    this._drawRoad(ctx);

    // entities far → near
    const sorted = [...this.entities].sort((a, b) => b.dz - a.dz);
    for (const en of sorted) {
      if (en.dz <= 0 || en.dz > SPAWN_DIST) continue;
      const s = this._screen(en.dz, en.laneNorm);
      if (en.type === "boost") this._drawBoost(ctx, s);
      else this._drawCar(ctx, s, en.color, false);
    }

    // player car
    const ps = this._screen(0, this.laneNorm);
    if (performance.now() < this.boostUntil) {
      ctx.save(); ctx.globalAlpha = 0.6; ctx.fillStyle = "#00e5ff";
      for (let i = 1; i <= 3; i++) { ctx.globalAlpha = 0.5 / i; ctx.beginPath(); ctx.ellipse(ps.x, ps.y + i * 12, 16, 6, 0, 0, 7); ctx.fill(); }
      ctx.restore();
    }
    this._drawCar(ctx, ps, "#e8f6ff", true);

    // particles
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.color;
      ctx.beginPath(); ctx.arc(p.x, p.y, 2.5, 0, 7); ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // HUD — speed readout
    ctx.fillStyle = "#00e5ff"; ctx.textAlign = "left"; ctx.textBaseline = "top";
    ctx.font = "900 15px 'Barlow Condensed', monospace";
    ctx.fillText(`${Math.round(this.speedVal * 2)} KM/H`, 10, 10);
    if (performance.now() < this.boostUntil) { ctx.fillStyle = "#5ff3ff"; ctx.fillText("BOOST!", 10, 28); }
  }

  _drawRoad(ctx) {
    const w = this.w, hy = this._horizonY, py = this._playerY;
    const nearHalf = w * 0.46, farHalf = 8, bottomHalf = w * 0.56;
    // grass shoulders
    ctx.fillStyle = "#12351f"; ctx.fillRect(0, hy, w, this.h - hy);
    // asphalt trapezoid
    ctx.fillStyle = "#26262c";
    ctx.beginPath();
    ctx.moveTo(w / 2 - farHalf, hy); ctx.lineTo(w / 2 + farHalf, hy);
    ctx.lineTo(w / 2 + bottomHalf, this.h); ctx.lineTo(w / 2 - bottomHalf, this.h);
    ctx.closePath(); ctx.fill();
    // red-white rumble strips (perspective bands, scrolling)
    for (let i = 0; i < 16; i++) {
      const p = (((i / 16) + this.dashOff) % 1);
      const pe = p * p; // ease for perspective
      const y = hy + (this.h - hy) * pe;
      const half = farHalf + (bottomHalf - farHalf) * pe;
      const band = i % 2 === 0 ? "#d13b3b" : "#e8e8ee";
      ctx.fillStyle = band; const t = Math.max(2, 10 * pe);
      ctx.fillRect(w / 2 - half - t, y, t, Math.max(1, 8 * pe));
      ctx.fillRect(w / 2 + half, y, t, Math.max(1, 8 * pe));
    }
    // dashed centre + lane lines
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    for (const lane of [-0.25, 0.25]) {
      for (let i = 0; i < 14; i++) {
        const p = (((i / 14) + this.dashOff) % 1); const pe = p * p;
        const y = hy + (this.h - hy) * pe;
        const half = farHalf + (nearHalf - farHalf) * pe;
        ctx.lineWidth = Math.max(1, 5 * pe);
        ctx.beginPath(); ctx.moveTo(w / 2 + lane * half * 2, y); ctx.lineTo(w / 2 + lane * half * 2, y + Math.max(2, 12 * pe)); ctx.stroke();
      }
    }
  }

  _drawCar(ctx, s, color, isPlayer) {
    const sc = s.scale;
    const carImg = isPlayer ? this._img(this.sprites.car) : null;
    if (carImg) { const cw = 60 * sc, ch = 84 * sc; ctx.drawImage(carImg, s.x - cw / 2, s.y - ch / 2, cw, ch); return; }
    const bw = 30 * sc, bh = 52 * sc;
    const x0 = s.x - bw / 2, y0 = s.y - bh / 2;
    // shadow
    ctx.fillStyle = "rgba(0,0,0,0.4)"; ctx.beginPath();
    ctx.ellipse(s.x, y0 + bh + 3 * sc, bw * 0.6, 5 * sc, 0, 0, 7); ctx.fill();
    // wheels
    ctx.fillStyle = "#0c0c10";
    [[x0 - 4 * sc, y0 + 6 * sc], [x0 + bw - 3 * sc, y0 + 6 * sc], [x0 - 4 * sc, y0 + bh - 20 * sc], [x0 + bw - 3 * sc, y0 + bh - 20 * sc]]
      .forEach(([wx, wy]) => ctx.fillRect(wx, wy, 7 * sc, 14 * sc));
    // rear wing
    ctx.fillStyle = color; ctx.fillRect(x0 - 5 * sc, y0 + bh - 6 * sc, bw + 10 * sc, 5 * sc);
    // body (F1 pointed nose toward horizon)
    ctx.fillStyle = color; ctx.strokeStyle = "rgba(0,0,0,0.5)"; ctx.lineWidth = Math.max(1, 1.4 * sc);
    ctx.beginPath();
    ctx.moveTo(s.x, y0);                       // nose
    ctx.lineTo(x0 + bw * 0.78, y0 + bh * 0.4);
    ctx.lineTo(x0 + bw, y0 + bh);              // rear right
    ctx.lineTo(x0, y0 + bh);                   // rear left
    ctx.lineTo(x0 + bw * 0.22, y0 + bh * 0.4);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    // cockpit
    ctx.fillStyle = isPlayer ? "#00344a" : "rgba(0,0,0,0.55)";
    ctx.beginPath(); ctx.ellipse(s.x, s.y, bw * 0.22, bh * 0.16, 0, 0, 7); ctx.fill();
    if (isPlayer) { ctx.strokeStyle = "#00e5ff"; ctx.lineWidth = 1.5 * sc; ctx.stroke(); }
  }

  _drawBoost(ctx, s) {
    const sc = s.scale, w = 60 * sc;
    ctx.save();
    ctx.globalAlpha = 0.85;
    for (let i = 0; i < 3; i++) {
      const yy = s.y - i * 8 * sc;
      ctx.fillStyle = "#00e5ff"; ctx.shadowBlur = 10 * sc; ctx.shadowColor = "#00e5ff";
      ctx.beginPath();
      ctx.moveTo(s.x - w / 2, yy + 6 * sc); ctx.lineTo(s.x, yy - 4 * sc); ctx.lineTo(s.x + w / 2, yy + 6 * sc);
      ctx.lineTo(s.x + w / 2, yy + 10 * sc); ctx.lineTo(s.x, yy); ctx.lineTo(s.x - w / 2, yy + 10 * sc);
      ctx.closePath(); ctx.fill();
    }
    ctx.restore(); ctx.shadowBlur = 0;
  }

  _loop(now) {
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this._update(dt);
    this._render();
    if (!this.over) this.raf = requestAnimationFrame(this._loop);
  }
}
